import { DescriptionOutlined, TuneRounded } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { LLMClassifierImplementationConfigurationType } from "models/LLMs/LLMClassifierImplementation";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import React, { useMemo } from "react";
import { getLLMClassifierVersionComparison } from "../../utils/llmClassifierVersionComparison";
import { groupVersionComparison } from "../../utils/llmClassifierVersionComparisonGroups";
import { llmClassifierVersionLabel } from "../../utils/llmClassifierVersionDisplay";

/** Compact "what changed" preview for the request dialog - the full diff is shown once the reviewer opens the review flow. */
export function ApprovalChangeSummary(props: {
    before?: LLMClassifierImplementation;
    after: LLMClassifierImplementation;
}) {
    const comparison = useMemo(
        () =>
            getLLMClassifierVersionComparison(
                props.before ?? emptyVersion(props.after),
                props.after,
            ),
        [props.before, props.after],
    );

    if (!comparison?.hasChanges) {
        return (
            <Typography color="gray.600" variant="body2">
                No configuration changes to review.
            </Typography>
        );
    }

    const beforeLabel = props.before
        ? llmClassifierVersionLabel(props.before.createdOn) ?? "Before"
        : "Initial state";
    const afterLabel =
        llmClassifierVersionLabel(props.after.createdOn) ?? "Requested version";
    const groups = groupVersionComparison(comparison);
    const hasPromptDiff = groups.some((group) => group.diffs.length > 0);
    const metadataLabels = [
        ...new Set(
            groups.flatMap((group) =>
                group.metadata.map((change) => change.label),
            ),
        ),
    ];

    const rows: { key: string; icon: React.ReactNode; label: string }[] = [
        ...(hasPromptDiff
            ? [
                  {
                      key: "prompt",
                      icon: (
                          <DescriptionOutlined
                              fontSize="small"
                              sx={{ color: "gray.600" }}
                          />
                      ),
                      label: `Prompt diff · ${beforeLabel} → ${afterLabel}`,
                  },
              ]
            : []),
        ...(metadataLabels.length > 0
            ? [
                  {
                      key: "metadata",
                      icon: (
                          <TuneRounded
                              fontSize="small"
                              sx={{ color: "gray.600" }}
                          />
                      ),
                      label: `Metadata changes · ${metadataLabels.join(", ")}`,
                  },
              ]
            : []),
    ];

    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: 2,
                overflow: "hidden",
            })}
        >
            {rows.map((row, index) => (
                <Stack
                    key={row.key}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    px={2}
                    py={1.5}
                    sx={(theme) => ({
                        borderTop:
                            index > 0
                                ? `1px solid ${theme.palette.gray[200]}`
                                : "none",
                    })}
                >
                    {row.icon}
                    <Typography variant="body2">{row.label}</Typography>
                </Stack>
            ))}
        </Box>
    );
}

function emptyVersion(
    version: LLMClassifierImplementation,
): LLMClassifierImplementation {
    const isPromptOnly =
        version.type ===
        LLMClassifierImplementationConfigurationType.PromptOnly;
    const configuration = isPromptOnly
            ? {
                  ...version.configuration,
                  promptTemplate: "",
                  questionText: "",
                  questionDescription: "",
                  executeConditions: [],
              }
            : {
                  ...version.configuration,
                  steps: [],
                  executeConditions: [],
              };

    return {
        ...version,
        id: "initial-state",
        configuration,
        llmResultDefinitions: [],
        parentModels: [],
    } as LLMClassifierImplementation;
}
